/**
 * Служебные слои (ADR 0021): данные для городских служб поверх той же карты.
 *
 * Здесь — три вещи, вокруг которых строится вся безопасность слоя:
 *  1. `LAYER_OF_KIND` — вид точки жёстко принадлежит одному слою. Нельзя
 *     положить опасное вещество в слой коммунальщиков, «чтобы увидели все».
 *  2. `publicVenue()` — единственный способ получить карту для публикации.
 *  3. `serviceFor()` — выдача по правам роли: чего роль не видит, того она
 *     не получает с сервера вовсе (а не прячется в интерфейсе).
 *
 * Модель доступа и роли — docs/core/CITY_SERVICES.md.
 */
import type { ServiceKind, ServiceLayer, ServicePoint, Venue } from './types'

/** Вид → слой. Источник истины: и валидация данных, и фильтрация выдачи. */
export const LAYER_OF_KIND: Readonly<Record<ServiceKind, ServiceLayer>> = {
  hydrant: 'fire',
  water_intake: 'fire',
  dry_riser: 'fire',
  fire_panel: 'fire',
  key_box: 'fire',
  electrical_panel: 'fire',
  shutoff_gas: 'fire',
  shutoff_water: 'fire',
  shutoff_power: 'fire',
  hazmat: 'fire',
  fire_lift: 'fire',
  vehicle_access: 'fire',
  ambulance_access: 'medical',
  stretcher_lift: 'medical',
  camera_registry: 'police',
  guard_post: 'police',
  riser: 'utility',
  meter: 'utility',
  waste_point: 'utility',
} as const

export const SERVICE_LAYERS: readonly ServiceLayer[] = [
  'fire',
  'medical',
  'police',
  'utility',
  'city',
] as const

export const SERVICE_KINDS = Object.keys(LAYER_OF_KIND) as readonly ServiceKind[]

/** Виды слоя в порядке объявления — для форм кабинета и легенды карты. */
export function kindsOfLayer(layer: ServiceLayer): readonly ServiceKind[] {
  return SERVICE_KINDS.filter((kind) => LAYER_OF_KIND[kind] === layer)
}

export function isServiceLayer(value: unknown): value is ServiceLayer {
  return typeof value === 'string' && (SERVICE_LAYERS as readonly string[]).includes(value)
}

export function isServiceKind(value: unknown): value is ServiceKind {
  return typeof value === 'string' && value in LAYER_OF_KIND
}

/**
 * Карта для публикации: служебная секция снимается.
 *
 * Возвращает venue без `service` ВСЕГДА — даже если секции не было. Так вызов
 * этой функции — единственный признак «данные готовы к отдаче наружу», и его
 * видно в диффе. Проверять `if (venue.service)` перед вызовом не нужно.
 */
export function publicVenue(venue: Venue): Venue {
  const { service: _service, ...rest } = venue
  return rest
}

/**
 * Точки, доступные роли с правами на `layers`.
 *
 * Слой `city` означает «видит все служебные слои» — это роль самого города,
 * глобального администратора карты. Остальные слои складываются.
 */
export function serviceFor(venue: Venue, layers: readonly ServiceLayer[]): readonly ServicePoint[] {
  const points = venue.service ?? []
  if (layers.includes('city')) return points
  const allowed = new Set(layers)
  return points.filter((point) => allowed.has(point.layer))
}

/** Сколько точек каждого слоя есть на площадке — для кабинета и панели города. */
export function serviceCounts(venue: Venue): Readonly<Record<ServiceLayer, number>> {
  const counts: Record<ServiceLayer, number> = {
    fire: 0,
    medical: 0,
    police: 0,
    utility: 0,
    city: 0,
  }
  for (const point of venue.service ?? []) counts[point.layer] += 1
  return counts
}

/**
 * Точки, которые пора перепроверить: без отметки о проверке или с отметкой
 * старше `maxAgeDays`. Возраст сведения — половина ценности служебного слоя:
 * гидрант, проверенный три года назад, для расчёта бесполезен.
 */
export function staleServicePoints(
  venue: Venue,
  now: Date,
  maxAgeDays = 365,
): readonly ServicePoint[] {
  const limit = now.getTime() - maxAgeDays * 24 * 60 * 60 * 1000
  return (venue.service ?? []).filter((point) => {
    const at = point.checked?.at
    if (at === undefined) return true
    const time = Date.parse(at)
    return !Number.isFinite(time) || time < limit
  })
}
